'use client';

import { useEffect, useRef } from 'react';
import { NETWORK_SETTINGS } from '@/app/constants';
import { useFetchStream } from '@/app/hooks';
import { type SessionEventBase, tryParseSessionEvent } from '../../_utils';
import { EVENTS_RECONNECT_DELAY_MS } from './helpers';
import type { SubscribeSessionEventsParams } from './types';

interface UseSessionEventsOptions {
  sessionId: string | null | undefined;
  /**
   * 历史里的最后 stream_seq，作为续传游标。
   * - undefined：历史尚未加载完，跳过订阅
   * - null：新建 session / 历史为空，订阅但不带 last_stream_seq
   * - string：作为 number 解析后传给后端
   */
  lastStreamSeq: string | null | undefined;
  /** 已 dedupe 过的事件回调，外部可以是不稳定引用（内部用 ref 转发）。 */
  onEvent: (event: SessionEventBase) => void;
  /** 每次消费到更大的 stream_seq 后回调给上层，供读游标上报使用。 */
  onStreamSeqAdvance?: (streamSeq: string) => void;
  /** 服务端正常断开 / 非 WAF 错误后，重连前的钩子（典型用法：拉 coordinator 快照对齐 phases）。 */
  onReconnect?: () => void;
}

/**
 * SSE 传输层：订阅 session events、按帧拆分、stream_seq 去重、断线重连。
 * 业务侧通过 onEvent 拿到已经过滤过的事件，不必关心传输细节。
 */
export const useSessionEvents = ({
  sessionId,
  lastStreamSeq,
  onEvent,
  onStreamSeqAdvance,
  onReconnect,
}: UseSessionEventsOptions) => {
  // 整个生命周期共享同一个 params 对象引用：useFetchStream 内部的自动重连
  // 与本文件 onClose/onError 的延迟重连，都能读到最新就地 mutate 的 last_stream_seq。
  const subscribeParamsRef = useRef<SubscribeSessionEventsParams>({ session_id: '' });

  const eventsBufferRef = useRef('');
  const processedSeqsRef = useRef<Set<number>>(new Set());
  const eventsReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 游标唯一真相源：按 session 维护"已见过的最大 stream_seq"。
  // 运行时事件推进它；effect 因 sessionMode 等 prop 抖动而重跑时不会让它退回历史值，
  // 只有 sessionId 真正切换时才用新的 lastStreamSeq 重置。subscribeParamsRef 从它派生。
  const cursorRef = useRef<{ sessionId: string; seq: number | undefined }>({ sessionId: '', seq: undefined });

  // ref 转发：避免外部回调身份变化导致 useFetchStream 闭包陈旧
  const onEventRef = useRef(onEvent);
  const onStreamSeqAdvanceRef = useRef(onStreamSeqAdvance);
  const onReconnectRef = useRef(onReconnect);
  onEventRef.current = onEvent;
  onStreamSeqAdvanceRef.current = onStreamSeqAdvance;
  onReconnectRef.current = onReconnect;

  const handleEventsMessageRef = useRef<(chunk: string) => void>(() => {});
  const handleEventsCloseRef = useRef<() => void>(() => {});
  const handleEventsErrorRef = useRef<() => void>(() => {});
  const runEventsStreamRef = useRef<((params: SubscribeSessionEventsParams) => void) | null>(null);
  const clearEventsReconnectRef = useRef<() => void>(() => {});

  const { run: runEventsStream, close: closeEventsStream } = useFetchStream<SubscribeSessionEventsParams>(
    '/claw/v1/claw/session/events/stream',
    {
      method: 'GET',
      baseURL: NETWORK_SETTINGS.baseUrl,
      // 心跳超时 / 可见性恢复的重连交给 useFetchStream 内部；
      // done:true 的正常断开和非 WAF 错误由下方 onClose/onError 兜底。
      onMessage: (chunk) => handleEventsMessageRef.current(chunk),
      onClose: () => handleEventsCloseRef.current(),
      onError: () => handleEventsErrorRef.current(),
    },
  );

  runEventsStreamRef.current = runEventsStream;

  const handleSessionEvent = (event: SessionEventBase) => {
    // stream_seq 去重 + 推进游标真相源（cursorRef），并同步到订阅参数
    if (typeof event.stream_seq === 'number' && Number.isFinite(event.stream_seq)) {
      if (processedSeqsRef.current.has(event.stream_seq)) return;
      processedSeqsRef.current.add(event.stream_seq);
      const current = cursorRef.current.seq;
      if (current === undefined || event.stream_seq > current) {
        cursorRef.current.seq = event.stream_seq;
        subscribeParamsRef.current.last_stream_seq = event.stream_seq;
        onStreamSeqAdvanceRef.current?.(String(event.stream_seq));
      }
    }
    onEventRef.current(event);
  };

  const processEventsFrame = (frame: string) => {
    // SSE 高频路径：避免 split + 多次 map/filter 的中间数组分配。
    for (const line of frame.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;
      const payload = trimmed.slice(5).trim();
      if (!payload || payload === 'heartbeat' || payload === '[DONE]') continue;
      const event = tryParseSessionEvent(payload);
      if (!event) continue;
      handleSessionEvent(event);
    }
  };

  const scheduleEventsReconnect = () => {
    if (eventsReconnectTimerRef.current) return;
    if (!subscribeParamsRef.current.session_id) return;
    eventsReconnectTimerRef.current = setTimeout(() => {
      eventsReconnectTimerRef.current = null;
      if (!subscribeParamsRef.current.session_id) return;
      runEventsStreamRef.current?.(subscribeParamsRef.current);
    }, EVENTS_RECONNECT_DELAY_MS);
  };

  const clearEventsReconnect = () => {
    if (eventsReconnectTimerRef.current) {
      clearTimeout(eventsReconnectTimerRef.current);
      eventsReconnectTimerRef.current = null;
    }
  };

  clearEventsReconnectRef.current = clearEventsReconnect;

  handleEventsMessageRef.current = (chunk: string) => {
    eventsBufferRef.current += chunk;
    const frames = eventsBufferRef.current.split('\n\n');
    eventsBufferRef.current = frames.pop() ?? '';
    for (const frame of frames) {
      processEventsFrame(frame);
    }
  };

  handleEventsCloseRef.current = () => {
    const tail = eventsBufferRef.current;
    if (tail.trim()) {
      eventsBufferRef.current = '';
      processEventsFrame(tail);
    }
    scheduleEventsReconnect();
    onReconnectRef.current?.();
  };

  handleEventsErrorRef.current = () => {
    scheduleEventsReconnect();
  };

  useEffect(() => {
    // lastStreamSeq === undefined 表示历史尚未加载完成，不发起订阅
    if (!sessionId || lastStreamSeq === undefined) {
      clearEventsReconnectRef.current();
      closeEventsStream();
      subscribeParamsRef.current.session_id = '';
      delete subscribeParamsRef.current.last_stream_seq;
      eventsBufferRef.current = '';
      return;
    }

    eventsBufferRef.current = '';
    clearEventsReconnectRef.current();

    const parsed = lastStreamSeq === null ? Number.NaN : Number(lastStreamSeq);

    const isNewSession = cursorRef.current.sessionId !== sessionId;
    if (isNewSession) {
      // 真正切换了 session：重置游标、去重集合，用历史 seq 作为初始游标
      cursorRef.current = { sessionId, seq: Number.isFinite(parsed) ? parsed : undefined };
      processedSeqsRef.current.clear();
    }
    // 同 session 内 prop 抖动（如 sessionMode undefined→default）：保留运行时游标，不退回历史值

    // 从游标真相源同步到订阅参数
    subscribeParamsRef.current.session_id = sessionId;
    const seq = cursorRef.current.seq;
    if (seq !== undefined) {
      subscribeParamsRef.current.last_stream_seq = seq;
    } else {
      // 新建 session / 历史为空：必须删掉 key，否则 GET 序列化会把 undefined 拼进 URL
      delete subscribeParamsRef.current.last_stream_seq;
    }
    runEventsStream(subscribeParamsRef.current);

    return () => {
      clearEventsReconnectRef.current();
      closeEventsStream();
    };
  }, [sessionId, lastStreamSeq, closeEventsStream, runEventsStream]);

  useEffect(() => {
    return () => {
      clearEventsReconnectRef.current();
    };
  }, []);
};
